<template>
  <div>
    <!-- HEADER -->
    <section class="mb-4 mb-lg-5">
      <div class="container">
        <div
          class="image-header"
          :class="{ 'rounded-regular': !authenticated }"
          :style="coverImage"
        ></div>
      </div>
    </section> <!-- END HEADER -->

    <!-- ACTIONS -->
    <section
      class="mb-3 mb-md-5"
      v-if="book"
    >
      <div class="container text-center d-flex flex-column flex-lg-row justify-content-center align-items-lg-center">
        <div :class="{'background-white': !nightMode, 'background-black': nightMode}" class="shadow-light rounded-xl p-2 pl-4 align-self-center d-flex align-items-center min-height-wrap">
          <div class="d-flex flex-wrap flex-md-nowrap flex-lg-wrap justify-content-center justify-content-md-between align-items-center">
            <router-link
              :to="{ name: 'categories.show', params: { username: book.user.username, slug: book.category.slug } }"
            >
              <div class="d-flex justify-content-center mr-4">
                <span class="text-color2 d-flex align-items-center">
                  <BaseIcon>
                    <TagIcon />
                  </BaseIcon>
                  <span class="text-color2 font-size-small">{{ book.category.name }}</span>
                </span>
              </div>
            </router-link>
            <div class="d-flex justify-content-center mr-4">
              <span class="text-color2 d-flex align-items-center">
                <BaseIcon>
                  <CalendarIcon />
                </BaseIcon>
                <span class="font-size-small">{{ book.published_at | formatDate }}</span>
              </span>
            </div>
            <div class="d-flex justify-content-center align-items-center">
              <div
                class="d-flex justify-content-center"
                :class="{ 'mr-4': book && user.id === book.user.id }"
                v-if="user && book"
              >
                <Button
                  class="btn btn-outline-color2 px-2 hover-color-white favorite-button"
                  :class="{ active: book.is_favorited }"
                  @click="toggleFavorite"
                  :loading="favoriteLoader"
                >
                  <BaseIcon>
                    <HeartFilledIcon v-if="book.is_favorited" />
                    <HeartIcon v-else />
                  </BaseIcon>
                </Button>
              </div>
              <div
                class="d-flex justify-content-center"
                v-else-if="book"
              >
                <Button
                  class="btn btn-outline-color2 px-2 hover-color-white favorite-button"
                  @click="likeBookButtonClick"
                >
                  <BaseIcon>
                      <HeartIcon />
                    </BaseIcon>
                </Button>
              </div>
              <div class="d-flex justify-content-center flex-grow-1">
                <router-link
                  class="btn btn-outline-color2"
                  :to="{ name: 'books.edit', params: { username: book.user.username, bookSlug: book.slug } }"
                  v-if="user && book.user.id === user.id"
                >
                  Editar
                </router-link>
              </div>
            </div>
          </div>
        </div>
        <div
          class="d-flex align-items-center flex-wrap flex-md-nowrap justify-content-center mt-3 mt-lg-0"
          v-if="book"
        >
          <UserTag
            class="ml-md-2 align-self-center mb-3 mb-md-0"
            :user="book.user"
          />
          <SaveToListTag
            class="ml-2 mr-2 align-self-center mb-3 mb-md-0"
            type="book"
            :target="book.id"
            v-if="user"
          />
          <!-- <UserDonationButton
            :user="book.user"
            id="author-post-desc"
            color="btn-outline-color2 mb-3 mb-md-0"
          /> -->
          <ShareTag
            class="ml-2 mr-2 align-self-center mb-3 mb-md-0"
            type="book"
            :target="book"
            v-if="book.status === 'published'"
          />
          <div v-if="user && user.id !== book.user.id">
            <Button
              class="btn-sm btn-outline-color2 px-3 mb-3 mb-md-0"
              v-b-modal.create-denounce
            >
              Denunciar
            </Button>
          </div>
          <div
            class="d-block d-md-inline-block ml-md-2 "
            v-else-if="user && user.id === book.user.id"
          >
            <Button
              class="btn-circle btn-outline-color2 d-inline-block mb-3 mb-md-0"
              v-b-modal="'destroy-' + book.type + '-' + book.id"
            >
              <i class="fa fa-trash"></i>
            </Button>
          </div>
        </div>
      </div>
    </section> <!-- END ACTIONS -->

    <DestroyBookModal
      :book="book"
      :type="book && book.type"
      :id="book && book.id"
    />

    <DenounceModal
      title="Denunciar Livro"
      type="book"
      v-if="book"
      :target="book.id"
    >
      <template v-slot:description>
        <p class="text-center font-size-small">
          Selecione a opção que melhor se encaixa com a sua denúncia. Nossa equipe avaliará o ocorrido e tomará as medidas necessárias.
        </p>
      </template>
    </DenounceModal>

    <!-- BOOK DATA -->
    <section class="mb-4" v-if="book">
      <div class="container text-center">
        <div class="col-lg-10 mx-auto">
          <h2 class="font-size-medium font-weight-bold mb-3">
            <BaseIcon
              v-if="user && book.visibility === 'private' && book.user.id === user.id"
            >
              <LockIcon />
            </BaseIcon>
            {{ book.title }}
          </h2>

          <div class="d-flex align-items-center justify-content-center">
            <span
              class="mr-4 font-size-small"
              v-b-modal.people-favorited
            >
              <BaseIcon
                size="xSmall"
              >
                <HeartFilledIcon />
              </BaseIcon>
              {{ book.favorites_count | formatNumber }}
            </span>
            <span class="mr-4 font-size-small">
              <BaseIcon
                size="xSmall"
              >
                <EyeIcon />
              </BaseIcon>
              {{ book.views_count | formatNumber }}
            </span>
            <span class="mr-4 font-size-small">
              <BaseIcon
                size="xSmall"
              >
                <CommentBubbleIcon />
              </BaseIcon>
              {{ book.comments_count | formatNumber }}
            </span>
            <span v-if="book && !book.has_files" class="mr-4 font-size-small">
              <BaseIcon
                size="xxSmall"
              >
                <HourglassIcon />
              </BaseIcon>
              {{ book && book.reading_time }}
            </span>
            <span v-if="book && book.number_of_pages" class=" font-size-small">
              <BaseIcon
                size="xxSmall"
              >
                <PageIcon />
              </BaseIcon>
              {{ book.number_of_pages }}
            </span>
          </div>
          <PeopleFavoritedModal
            :target="book.id"
            type="book"
          />

          <p
            class="mt-4"
            v-if="book.description"
          >
            {{ book.description }}
          </p>
        </div>
      </div>
    </section> <!-- END BOOK DATA -->

    <!-- AWARDS -->
    <section
      class="mb-4 awards"
      v-if="awards.length > 0"
    >
      <div class="container">
        <AwardList
          type="book"
          :target="book.id"
          :awards="awards"
        />
      </div>
    </section> <!-- END AWARDS -->

    <!-- STATS -->
    <section v-if="owned" class="mb-4 mt-4">
      <div class="container text-center">
        <h2 class="font-weight-bold font-size-medium text-center">Estatísticas</h2>
        <p class="mb-0"><strong>Valor de venda:</strong> {{ (book.amount/100) | formatMoney }}</p>
        <p class="mb-0" v-if="book.amount > 0"><strong>Vendas:</strong> {{ book.sales }}</p>
      </div>
    </section> <!-- END STATS -->

    <!-- PURCHASE -->
    <template v-if="book && !book.can_current_user_read && book.amount > 0">
      <!-- BOOK WAITING PAYMENT -->
      <div
        class="text-center my-5 mx-auto col-md-6"
        v-if="book.is_current_waiting_payment"
      >
        <div>
          <BaseIcon
            size="medium"
          >
            <LockIcon />
          </BaseIcon>
        </div>
        <h2 class="font-weight-bold font-size-medium text-color2">Acesso restrito</h2>
        <p>Estamos aguardando seu pagamento! Verifique sua caixa de e-mail ou acesse <router-link :to="{ name: 'orders.index' }" class="font-weight-bold link-color2">seus pedidos</router-link> para visualizar instruções de como finalizar sua compra.</p>
      </div> <!-- END BOOK WAITING PAYMENT -->
      <!-- BOOK PURCHASE -->
      <div
        v-else
      >
        <!-- BOOK PROCESSING PAYMENT -->
        <div
          class="text-center my-5 mx-auto col-md-6"
          v-if="book.is_processing_payment"
        >
          <div>
            <BaseIcon
              size="medium"
            >
              <LockIcon />
            </BaseIcon>
          </div>
          <h2 class="font-weight-bold font-size-medium text-color2">Acesso restrito</h2>
          <p>Estamos processando seu pagamento! Em breve você receberá um aviso por e-mail com mais instruções!</p>
        </div> <!-- END BOOK PROCESSING PAYMENT -->
        <!-- PURCHASE BOOK -->
        <div v-else>
          <div class="mt-4 mb-5 text-center">
            <div>
              <BaseIcon
                size="medium"
              >
                <LockIcon />
              </BaseIcon>
            </div>
            <h2 class="font-weight-bold font-size-medium text-color2">Acesso restrito</h2>
            <p>Para ter acesso completo a esse livro, compre agora mesmo por <span class="font-weight-bold text-color2">{{ book.amount/100 | formatMoney }}</span>!</p>
            <div>
              <Button
                type="button"
                class="btn-outline-color2"
                @click="buyBookButtonClick"
              >
                Comprar
              </Button>
              <Button
                type="button"
                class="btn-outline-color2 ml-2"
                @click="buyBookScrivProClick"
                v-if="user && user.is_scriv_pro_enabled"
              >
                Liberar com Scriv Pro
              </Button>
              <ScrivProBookPurchaseModal
                :book="book"
                v-if="user && user.is_scriv_pro_enabled"
                @purchase-completed="reset"
              />
            </div>
          </div>
        </div> <!-- END PURCHASE BOOK -->
        <TheCheckoutModal
          v-if="user && (!book.is_processing_payment || justPurchased)"
          :products="[{
            id: book.id,
            slug: book.slug,
            type: 'book'
          }]"
          :paymentMethods="book.available_payment_methods"
          @purchase-completed="purchaseCompleted"
        />
      </div> <!-- END BOOK PURCHASE -->
    </template>
    <!-- END PURCHASE -->

    <!-- GENRES -->
    <section class="mt-3" v-if="book && book.genres.length > 0">
      <div class="container">
        <div class="col-12 col-lg-10 mx-auto">
          <h2 class="font-weight-bold font-size-medium text-center">Gêneros</h2>
          <ul class="tag-list d-flex mt-3 flex-wrap justify-content-center">
            <li class="tag-item mb-2 mr-4"
              v-for="genre in book.genres"
              :key="genre.id"
            >
              <span class="tag">{{ genre.name }}</span>
            </li>
          </ul>
        </div>
      </div>
    </section> <!-- END GENRES -->

    <!-- FILES -->
    <section class="mt-3 mb-5" v-if="book && (book.files && book.files.length > 0 || owned)">
      <div class="container">
        <div class="col-12 col-lg-10 mx-auto">
          <h2 class="font-weight-bold font-size-medium text-center mb-3">Faça o download agora</h2>
          <ul class="d-flex list-unstyled justify-content-center align-items-center flex-column flex-md-row" v-if="book.files && book.files.length > 0">
            <li v-for="file in book.files" :key="file.id">
              <Button
                class="btn btn-outline-color2 btn-sm mr-2"
                :loading="downloadLoader === file.type"
                @click="downloadFile(file)"
              >
                Baixar {{ file.extension.toUpperCase() }}
              </Button>
            </li>
            <li v-if="book.has_epub">
              <Button
                class="btn btn-outline-color2 btn-sm"
                v-b-modal="'send-book-to-kindle-modal'"
              >
                Enviar para Kindle
              </Button>
            </li>
          </ul>
          <SendBookToKindleModal
            :book="book"
          />
          <div class="text-center" v-if="owned">
            <router-link
              class="btn btn-outline-color2"
              :to="{ name: 'books.file.upload', params: { book: book.slug } }"
            >
              Gerenciar arquivos
            </router-link>
          </div>
        </div>
      </div>
    </section><!-- END FILES -->

    <!-- CHAPTER LIST -->
    <section
      v-if="book"
    >
      <div class="container">
        <!-- UNLOCKED CHAPTER LIST -->
        <template v-if="!hasLoadedChapters || chapters.length > 0">
          <h3 class="font-size-medium font-weight-bold text-color2 mb-3 text-center">Lista de Capítulos</h3>
          <template v-if="owned">
            <!-- CREATE NEW CHAPTER -->
            <div
              class="mt-4 text-center"
            >
              <router-link
                class="btn btn-outline-color2 btn mb-4"
                :to="{ name: 'chapters.create', params: { bookSlug: book.slug } }"
              >
                Criar novo
              </router-link>
            </div> <!-- END CREATE NEW CHAPTER -->
            <!-- TOGGLE VISIBILITY -->
            <div class="col-md-7 mx-md-auto text-center mb-4">
              <button
                type="button"
                class="btn btn-outline-color2 btn-sm mr-3"
                @click="openToggleVisibilityModal('public')"
              >
                Tornar tudo público
              </button>
              <button
                type="button"
                class="btn btn-outline-color2 btn-sm"
                @click="openToggleVisibilityModal('private')"
              >
                Tornar tudo privado
              </button>
            </div> <!-- END TOGGLE VISIBILITY -->
            <ToggleChaptersVisibilityModal
              :book="book"
              :visibility="toggleVisibility"
              @chapters-visibility-changed="getChapters(true)"
            />
          </template>
          <div
            class="chapter-list col-md-7 mx-md-auto"
            :class="{ disabled: this.loaderReorder }"
            v-if="chapters.length > 0"
          >
            <template
              v-if="owned"
            >
              <draggable
                v-model="chapters"
                group="chapters"
                @start="drag=true"
                @end="drag=false"
                @change="sortChapters"
              >
                <ChapterListItem
                  v-for="(chapter, index) in chapters"
                  :key="chapter.id"
                  :chapter="chapter"
                  :orderNumber="index"
                  :canUserReadBook="true"
                />
              </draggable>
            </template>
            <template
              v-else
            >
              <ChapterListItem
                v-for="(chapter, index) in chapters"
                :key="chapter.id"
                :chapter="chapter"
                :orderNumber="index"
                :canUserReadBook="book.can_current_user_read"
              />
            </template>
            <div
              class="mt-3 mb-5 text-center"
              v-if="isLoadMoreChaptersVisible"
            >
              <Button
                class="btn-outline-color2"
                :loading="chaptersLoader"
                @click="loadMoreChapters"
              >
                Carregar mais
              </Button>
            </div>
          </div>
          <div
            class="text-center"
            v-else-if="!chaptersLoader"
          >
            <p>Essa obra não possui capítulos publicados ou disponíveis para leitura</p>
          </div>
          <div
            class="text-center"
            v-else
          >
            <Loader
              color="color2"
              size="big"
            />
          </div>
          <p class="font-size-xSmall mt-2 text-color2 mt-2 mb-0 text-center col-md-6 mx-md-auto" v-if="owned"><strong>Atenção:</strong> O livro só aparecerá para os usuários se tiver um capítulo publicado ou arquivo associado. Até lá, apenas você poderá ver, independente da privacidade selecionada.</p>
        </template> <!-- END UNLOCKED CHAPTER LIST -->
      </div>
    </section> <!-- END CHAPTER LIST -->

    <!-- TAGS -->
    <section class="mt-3" v-if="book && book.tags.length > 0">
      <div class="container">
        <div class="col-12 col-lg-10 mx-auto">
          <h2 class="font-weight-bold font-size-medium text-center">Tags</h2>
          <ul class="tag-list d-flex mt-3 flex-wrap justify-content-center">
            <li class="tag-item mb-2 mr-4"
              v-for="tag in book.tags"
              :key="tag.id"
            >
              <router-link
                :to="{ name: 'tags.show', params: { slug: tag.slug } }"
              >
                <span class="tag">{{ tag.name }}</span>
              </router-link>
            </li>
          </ul>
        </div>
      </div>
    </section> <!-- END TAGS -->

    <!-- LICENSE -->
    <section class="mt-3" v-if="book && book.license">
      <div class="container">
        <div class="col-12 col-lg-10 mx-auto text-center">
          <h2 class="font-weight-bold font-size-medium">
            Licença
            <i
              class="fa fa-info-circle cursor-pointer text-color2 font-size-small"
              v-b-tooltip.hover="{ customClass: 'font-size-xSmall' }"
              :title="licenseTooltipText"
            ></i>
          </h2>
          <span class="text-uppercase" v-b-tooltip.hover="{ customClass: 'font-size-xSmall' }" :title="licenseTipText" placement="bottom">
            <i class="text-color2" :class="licenseClasses"></i> {{ book && book.license }}
          </span>
        </div>
      </div>
    </section> <!-- END LICENSE -->

    <!-- SHARE -->
    <section class="mt-3" v-if="book && book.status === 'published'">
      <div class="container">
        <hr
          class="is-empty h-1 col-10 mx-auto my-4 w-75"
        />
        <div class="col-12 col-lg-10 mx-auto text-center">
          <h2 class="font-weight-bold font-size-medium mb-4">Compartilhe este livro</h2>
          <Button
            class="btn btn-outline-color2 px-2 hover-color-white col-6 col-offset-3 col-md-2 col-md-offset-2 mr-2"
            :class="{ disabled: !book }"
            type="book"
            :target="book"
            v-b-modal="'share-' + book.type + '-' + book.id"
          >
            Compartilhar
          </Button>
        </div>
      </div>
    </section> <!-- END SHARE -->

    <!-- COMMENTS -->
    <section class="mt-5" v-if="book && book.is_comments_enabled && book.status === 'published'">
      <div class="container">
        <div class="col-12 col-lg-10 mx-auto text-center text-lg-left">
          <h2 class="font-weight-bold font-size-medium">Comentários</h2>

          <CommentSection
            :entityId="book.id"
            entityType="book"
            :isAuthor="currentUser && (book.user.id === currentUser.id)"
          />

        </div>
      </div>
    </section> <!-- END COMMENTS -->

  </div>
</template>

<script>

import { mapState } from 'vuex'
import AwardsService from '@/modules/awards/services/awards-service'
import BooksService from '@/modules/books/services/books-service'
import ChaptersService from '@/modules/books/services/chapters-service'
import FavoritesService from '@/modules/favorites/services/favorites-service'
import variables from '@/mixins/variables'
import seo from '@/mixins/seo'
import draggable from 'vuedraggable'

import AwardList from '@/components/awards/AwardList'
import Button from '@/components/common/Button/Button.vue'
import ChapterListItem from '@/components/chapters/ChapterListItem.vue'
import CommentSection from '@/components/comment/CommentSection'
import DenounceModal from '@/components/denounces/DenounceModal.vue'
import DestroyBookModal from '@/components/books/DestroyBookModal.vue'
import Loader from '@/components/common/Loader/Loader.vue'
import PeopleFavoritedModal from '@/components/favorites/PeopleFavoritedModal.vue'
import SaveToListTag from '@/components/lists/SaveToListTag.vue'
import ScrivProBookPurchaseModal from '@/components/books/ScrivProBookPurchaseModal'
import SendBookToKindleModal from '@/components/books/SendBookToKindleModal'
import ShareTag from '@/components/share/ShareTag.vue'
import UserTag from '@/components/user/UserTag.vue'
import TheCheckoutModal from '@/components/checkout/TheCheckoutModal'
import ToggleChaptersVisibilityModal from '@/components/chapters/ToggleChaptersVisibilityModal'
// import UserDonationButton from '@/components/user/UserDonationButton'

import BaseIcon from '@/components/common/Icon/BaseIcon.vue'
import CalendarIcon from '@/components/common/Icon/CalendarIcon.vue'
import CommentBubbleIcon from '@/components/common/Icon/CommentBubbleIcon.vue'
import EyeIcon from '@/components/common/Icon/EyeIcon.vue'
import HeartIcon from '@/components/common/Icon/HeartIcon.vue'
import HeartFilledIcon from '@/components/common/Icon/HeartFilledIcon.vue'
import HourglassIcon from '@/components/common/Icon/HourglassIcon.vue'
import LockIcon from '@/components/common/Icon/LockIcon.vue'
import PageIcon from '@/components/common/Icon/PageIcon'
import TagIcon from '@/components/common/Icon/TagIcon.vue'

export default {
  mixins: [variables, seo],
  components: {
    AwardList,
    BaseIcon,
    Button,
    CalendarIcon,
    CommentBubbleIcon,
    CommentSection,
    ChapterListItem,
    DenounceModal,
    DestroyBookModal,
    draggable,
    EyeIcon,
    HeartIcon,
    HeartFilledIcon,
    HourglassIcon,
    Loader,
    LockIcon,
    PeopleFavoritedModal,
    SaveToListTag,
    ScrivProBookPurchaseModal,
    SendBookToKindleModal,
    ShareTag,
    TagIcon,
    TheCheckoutModal,
    ToggleChaptersVisibilityModal,
    // UserDonationButton,
    UserTag,
    PageIcon
  },
  data () {
    return {
      book: null,
      awards: [],
      chapters: [],
      favoriteLoader: false,
      chaptersLoader: false,
      loaderReorder: false,
      toggleVisibility: 'public',
      hasLoadedChapters: false,
      justPurchased: false,
      downloadLoader: false,
      chaptersPage: 1,
      isLoadMoreChaptersVisible: true
    }
  },
  metaInfo () {
    return {
      title: this.book ? this.book.title : '',
      meta: [
        { vmid: 'description', name: 'description', content: this.book && this.book.description ? this.book.description : this.seoDefaultDescription },
        { vmid: 'og-description', name: 'og:description', content: this.book && this.book.description ? this.book.description : this.seoDefaultDescription },
        { vmid: 'og-title', name: 'og:title', content: this.book ? `${this.book.title} | ${this.seoDefaultTitleAppend}` : `${this.seoDefaultTitle} | ${this.seoDefaultTitleAppend}` },
        { vmid: 'og-image', name: 'og:image', content: this.book && this.book.has_cover ? this.book.cover : this.seoDefaultImage }
      ]
    }
  },
  created () {
    this.getBook()
  },
  computed: {
    ...mapState({
      nightMode: state => state.nightMode
    }),
    ...mapState('users', {
      currentUser: state => state.user
    }),
    coverImage () {
      return {
        backgroundImage: this.book ? `url('${this.book.cover}')` : `url('${this.noCoverUrl}')`
      }
    },
    profileImage () {
      return {
        backgroundImage: this.book ? `url('${this.book.user.avatar}')` : `url('${this.noAvatarUrl}')`
      }
    },
    owned () {
      return this.user && this.book && this.user.id === this.book.user.id
    },
    ...mapState('users', {
      user: state => state.user
    }),
    ...mapState('auth', {
      authenticated: state => state.authenticated
    }),
    selectedLicense () {
      if (!this.book || !this.book.license) return null
      return this.licenseOptions.find(item => {
        return item.key === this.book.license
      })
    },
    licenseTipText () {
      if (!this.selectedLicense) return ''
      return this.selectedLicense.description || ''
    },
    licenseClasses () {
      if (!this.selectedLicense) return []
      return [this.selectedLicense.icon]
    }
  },
  methods: {
    async getBook (getChapters = true, getComments = true, getAwards = true) {
      try {
        const book = await BooksService.getBook(this.$route.params.slug)
        this.book = book
        if (getChapters) this.getChapters(true)
        if (getAwards) this.getAwards()
      } catch (error) {
        error.handleGlobally && error.handleGlobally()
        this.$router.push('/')
      }
    },
    async getChapters (reload = false) {
      if (reload) {
        this.chapters = []
        this.chaptersPage = 1
      }
      if (this.book) {
        this.chaptersLoader = true
        try {
          const chapters = await ChaptersService.getChapters({
            bookId: this.book.id,
            page: this.chaptersPage
          })
          this.chapters = [...this.chapters, ...chapters]
          if (chapters.length <= 0) this.isLoadMoreChaptersVisible = false
          this.hasLoadedChapters = true
        } catch (error) {
          error.handleGlobally && error.handleGlobally()
        }
        this.chaptersLoader = false
      }
    },
    async toggleFavorite () {
      this.favoriteLoader = true
      try {
        const action = this.book.is_favorited ? 'destroy' : 'create'
        await FavoritesService.toggleFavorite(this.book.id, 'book', action)
        this.book.is_favorited = !this.book.is_favorited
        this.getBook(false, false, false)
      } catch (error) {
        error.handleGlobally && error.handleGlobally()
      }
      this.favoriteLoader = false
    },
    async sortChapters (arg) {
      if (arg.moved) {
        this.loaderReorder = true
        const element = arg.moved.element
        const newPos = arg.moved.newIndex + 1
        try {
          await ChaptersService.reorderChapter({ id: element.id, orderNumber: newPos })
        } catch (error) {
          error.handleGlobally && error.handleGlobally()
        }
        this.loaderReorder = false
      }
    },
    async getAwards () {
      try {
        const awards = await AwardsService.getAwards({ type: 'book', target: this.book.id })
        this.awards = awards
      } catch (error) {
        error.handleGlobally && error.handleGlobally()
      }
    },
    buyBookButtonClick () {
      if (!this.book) return false
      if (!this.authenticated) {
        this.$bvModal.show('auth-modal')
        return
      }
      this.$bvModal.show('checkout-modal')
    },
    buyBookScrivProClick () {
      if (!this.book || !this.authenticated) return false
      this.$bvModal.show('scriv-pro-book-purchase-modal')
    },
    likeBookButtonClick () {
      if (!this.authenticated) {
        this.$bvModal.show('auth-modal')
      }
    },
    purchaseCompleted (invoice) {
      this.book.is_processing_payment = true
      this.justPurchased = true
      // this.getBook()
    },
    openToggleVisibilityModal (visibility) {
      this.toggleVisibility = visibility
      this.$bvModal.show('toggle-chapters-visibility')
    },
    async downloadFile (file) {
      this.downloadLoader = file.type
      try {
        const download = await BooksService.downloadBookFile(this.book.id, file.id)
        const type = file.extension === 'epub' ? 'application/epub+zip' : 'application/pdf'
        const blob = new Blob([download], { type: type })
        const link = document.createElement('a')
        link.href = window.URL.createObjectURL(blob)
        link.download = `${file.filename}.${file.extension}`
        link.click()
      } catch (error) {
        error.handleGlobally && error.handleGlobally()
      }
      this.downloadLoader = false
    },
    reset () {
      this.book = null
      this.chaptersPage = 1
      this.getBook()
    },
    loadMoreChapters () {
      this.chaptersPage++
      this.getChapters()
    }
  }
}
</script>

<style lang="scss" scoped>
.favorite-button {
  height: 44px;
  width: 46px;
  ::v-deep {
    & > span {
      display: block;
      margin-top: -1px;
    }
  }
}

.chapter-list {
  ::v-deep {
    .chapter-item {
      // border-bottom: 1px solid $color2;
      padding: 20px 15px;
    }

    .item:last-child {
      .chapter-item {
        // border: none;
      }
    }
  }

  &.disabled {
    opacity: 0.7;
    pointer-events: none;
  }
}
</style>
